import { useRef, useState, type ComponentType } from 'react'

import ConfirmModal from '../components/ConfirmModal/ConfirmModal'

interface Props<T> {
  title: string
  description: string
  confirmButton?: string
  onConfirm?: (param: T) => void
  onCancel?: () => void
}

interface ModalState<T> {
  param: T | null
  isOpen: boolean
  close: () => void
}

const useConfirmModal = <T,>() => {
  const [param, setParam] = useState<T | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const close = () => {
    setParam(null)
    setIsOpen(false)
  }

  // The component handed back to callers must keep ONE identity for the life of
  // the hook. Defining it inline on every render gives React a new component
  // type each time, which unmounts and remounts the AlertDialog underneath —
  // on pages that poll (clones list/details every 5s) an open dialog visibly
  // flickered on every tick. The component is created once and reads the live
  // state through a ref that the hook refreshes on each render; the caller
  // re-renders it whenever this state changes, so the ref is never stale.
  const stateRef = useRef<ModalState<T>>({ param, isOpen, close })
  stateRef.current = { param, isOpen, close }

  const [ConfirmationModal] = useState<ComponentType<Props<T>>>(() => {
    const BoundConfirmModal = ({
      title,
      description,
      confirmButton = 'Confirm',
      onCancel,
      onConfirm
    }: Props<T>) => {
      const { param, isOpen, close } = stateRef.current

      const confirmOpen = () => {
        onConfirm?.(param as T)
        close()
      }

      const confirmCancel = () => {
        onCancel?.()
        close()
      }

      return (
        <ConfirmModal
          isOpen={isOpen}
          confirm={confirmOpen}
          close={confirmCancel}
          title={title}
          description={description}
          confirmButton={confirmButton}
        />
      )
    }
    return BoundConfirmModal
  })

  const open = (param: T) => {
    setParam(param)
    setIsOpen(true)
  }

  return { openConfirm: open, ConfirmationModal }
}

export default useConfirmModal
